"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrCadDSNTransformer = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const alphaDesignatorToConstant_1 = require("../../common/alphaDesignatorToConstant");
const addMetadataWithoutDuplicates_1 = require("../../common/addMetadataWithoutDuplicates");
const orcadLayers_1 = require("../orcadLayers");
const Font_1 = require("../../Font");
const fonts_1 = require("../../Fonts/fonts");
const orcadShapeConverters_1 = require("../orcadShapeConverters");
const Area_1 = require("../../Area");
const orcadDsnPhysicalPartFallback_1 = require("./orcadDsnPhysicalPartFallback");
class OrCadDSNTransformer {
    static { this.DEFAULT_WIRE_WIDTH = 0.3; }
    static { this.WIRE_WIDTH_MULTIPLIER = 0.1; }
    constructor(schemas) {
        this.globalSymbolNum = 1;
        this.offPageSymbols = {};
        this.portSymbols = {};
        this.schematicHierarchies = [];
        this.logicalSchemas = new Map(schemas.map((s) => [s.Name, s]));
        this.project = {
            type: 'orcad',
            layerSets: {},
            designs: {},
            components: {},
            modules: [],
            warnings: [],
            processingResults: {
                type: 'project-processing',
                message: 'Project processed successfully',
                innerResults: [],
            },
            fonts: {
                fonts: {},
            },
            globalNets: {},
        };
    }
    transformToProjectModel(netlist) {
        for (const [, design] of this.logicalSchemas) {
            if (design.Cache) {
                this.extractComponentsFromCache(design.Cache);
            }
            let sheetIndex = 0;
            for (const schematic of design.Schematic) {
                this.extractSchematicFromSchematic(design, schematic, sheetIndex++);
            }
        }
        if (netlist) {
            this.applyNetlist(netlist);
        }
        else {
            this.logProcessResult({
                type: 'netlist-missing',
                message: 'No netlist provided. Module pin nets will not be populated.',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
            });
        }
        (0, Font_1.packModelFonts)(this.project);
        return this.project;
    }
    logProcessResult(result) {
        this.project.processingResults.innerResults.push(result);
    }
    extractComponentsFromCache(cache) {
        for (const pkg of cache.Package ?? []) {
            for (const libPart of pkg.LibPart) {
                this.extractComponentFromLibPart(pkg, libPart);
            }
        }
        for (const globalSymbol of cache.GlobalSymbol ?? []) {
            this.extractComponentFromGlobalSymbol(globalSymbol);
        }
        for (const portSymbol of cache.PortSymbol ?? []) {
            this.extractPortSymbol(portSymbol);
        }
        for (const offPageSymbol of cache.OffPageSymbol ?? []) {
            this.extractOffPageSymbol(offPageSymbol);
        }
    }
    extractComponentFromGlobalSymbol(globalSymbol) {
        const bboxCenter = [
            (globalSymbol.BoundingBox.X1 + globalSymbol.BoundingBox.X2) / 2,
            (globalSymbol.BoundingBox.Y1 + globalSymbol.BoundingBox.Y2) / 2,
        ];
        const pointTransform = (p) => {
            return (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
        };
        const componentSymbol = {
            ref: globalSymbol.Name,
            name: globalSymbol.Name,
            drawings: orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(globalSymbol).map((d) => {
                return {
                    layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
                    drawing: d,
                };
            }),
            pins: globalSymbol.PinScalar?.map((p) => {
                return {
                    type: 'pin',
                    ref: p.Position.toString() || p.Name,
                    name: p.Name,
                    number: p.Position.toString() || p.Name,
                    start: pointTransform([p.StartX, p.StartY]),
                    end: pointTransform([p.HotptX, p.HotptY]),
                    layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
                    drawings: [],
                    metadata: {
                        ['orcad.pin.position']: p.Position,
                    },
                };
            }),
            metadata: {
                'orcad.symbol.boundingbox': globalSymbol.BoundingBox,
            },
        };
        const component = {
            name: globalSymbol.Name,
            numPins: componentSymbol.pins.length,
            numPads: 0,
            footprints: [],
            symbols: [componentSymbol],
            signals: componentSymbol.pins.map((p) => ({ id: p.ref, name: p.name, pinRefs: [p.ref], padRefs: [] })),
            isVirtual: true,
            metadata: {
                'orcad.globalSymbol.name': globalSymbol.Name,
                'orcad.globalSymbol.sourceLibName': globalSymbol.SourceLibName,
            },
        };
        this.project.components[component.name] = component;
    }
    extractComponentFromLibPart(pkg, libPart) {
        const componentRef = `${pkg.SourceLibName}::${pkg.Name}`;
        let component = this.project.components[componentRef];
        if (component) {
            if (!component.metadata) {
                component.metadata = {};
            }
            component.metadata['orcad.package.sectionCount'] =
                (component.metadata['orcad.package.sectionCount'] ?? 0) + 1;
        }
        else {
            component = {
                name: componentRef,
                numPins: 0,
                numPads: 0,
                footprints: [],
                symbols: [],
                signals: [],
                metadata: {
                    'orcad.package.alphabeticNumbering': pkg.AlphabeticNumbering,
                    'orcad.package.isHomogeneous': pkg.IsHomogeneous,
                    'orcad.package.name': pkg.Name,
                    'orcad.package.pcbFootprint': pkg.PcbFootprint,
                    'orcad.package.pcbLib': pkg.PcbLib,
                    'orcad.package.refdesPrefix': pkg.RefdesPrefix,
                    'orcad.package.sourceLibName': pkg.SourceLibName,
                },
            };
        }
        if (libPart.NormalView && libPart.NormalView.length > 1) {
            throw new Error(`Not sure what to do with multiple normal views for component ${component.name}`);
        }
        const physicalParts = libPart.PhysicalPart?.length ? libPart.PhysicalPart : [(0, orcadDsnPhysicalPartFallback_1.synthesizePhysicalPartFromPins)(libPart)];
        if (!libPart.PhysicalPart?.length) {
            this.logProcessResult({
                type: 'orcad-dsn-physical-part-missing',
                message: `Synthesized a physical part for ${pkg.SourceLibName}::${pkg.Name}::${libPart.CellName}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        for (const physicalPart of physicalParts) {
            const normalView = libPart.NormalView?.[0];
            const normalViewPinCount = (normalView?.PinScalar?.length ?? 0) + (normalView?.PinBus?.length ?? 0);
            if (physicalPart.PinNumber && physicalPart.PinNumber.length !== normalViewPinCount) {
                throw new Error(`PhysicalPart.PinNumber length does not match NormalView pin count for ${component.name}::${libPart.CellName}`);
            }
        }
        for (const physicalPart of physicalParts) {
            const physicalPartIndex = physicalParts.indexOf(physicalPart);
            let physicalPartSuffix = '';
            if (physicalParts.length > 1) {
                physicalPartSuffix = `-${physicalPartIndex + 1}`;
            }
            const pinPositionMap = new Map();
            for (const pinNumber of physicalPart.PinNumber ?? []) {
                if (pinPositionMap.has(pinNumber.Position)) {
                    throw new Error(`Duplicate pin position ${pinNumber.Position} found in physical part for ${component.name}::${libPart.CellName}`);
                }
                pinPositionMap.set(pinNumber.Position, pinNumber.Number.toString());
            }
            for (const normalView of libPart.NormalView ?? []) {
                const viewSignalNames = new Set();
                const ref = `${libPart.CellName}${normalView.Suffix}${physicalPartSuffix}`;
                const metadata = {
                    'orcad.symbol.boundingbox': normalView.BoundingBox,
                    'orcad.symbol.sectionIndex': component.metadata?.['orcad.package.sectionCount'] ?? 0,
                };
                if (physicalPartSuffix) {
                    metadata['orcad.symbol.physicalPartIndex'] = physicalPartIndex;
                }
                const bboxCenter = [
                    (normalView.BoundingBox.X1 + normalView.BoundingBox.X2) / 2,
                    (normalView.BoundingBox.Y1 + normalView.BoundingBox.Y2) / 2,
                ];
                const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
                const symbol = {
                    ref,
                    name: ref,
                    drawings: [
                        ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(normalView).map((drawing) => {
                            return {
                                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
                                drawing,
                            };
                        }),
                        ...(normalView.CommentText ?? []).map((c) => ({
                            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
                            drawing: {
                                type: 'text',
                                value: c.Name,
                                pos: pointTransform([c.LocX, c.LocY]),
                                angle: c.Rotation ?? 0,
                                justify: [-1, 0],
                                size: this.scaleFont(c.Font),
                                thickness: 1,
                            },
                        })),
                    ],
                    pins: [...(normalView.PinScalar ?? []), ...(normalView.PinBus ?? [])].flatMap((p) => {
                        let pinRef = pinPositionMap.get(p.Position);
                        if (pinRef === undefined) {
                            throw new Error(`Pin position ${p.Position} not found in pin position map`);
                        }
                        if (pinRef === '') {
                            pinRef = p.Name;
                        }
                        if (viewSignalNames.has(p.Name)) {
                            throw new Error(`Duplicate signal name ${p.Name} found in normal view ${normalView.Suffix}`);
                        }
                        let signal = component.signals.find((s) => s.name === p.Name);
                        if (!signal) {
                            signal = {
                                id: p.Name,
                                name: p.Name,
                                pinRefs: [pinRef],
                                padRefs: [],
                            };
                            component.signals.push(signal);
                        }
                        else {
                            signal.pinRefs.push(pinRef);
                        }
                        const metadata = {
                            ['orcad.pin.position']: p.Position,
                        };
                        if (p.IsNoConnect) {
                            metadata['orcad.pin.isNoConnect'] = true;
                        }
                        return {
                            type: 'pin',
                            ref: pinRef,
                            name: p.Name,
                            number: pinRef,
                            start: pointTransform([p.StartX, p.StartY]),
                            end: pointTransform([p.HotptX, p.HotptY]),
                            layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
                            drawings: this.generatePinText(p, pinRef, pointTransform),
                            metadata,
                        };
                    }),
                    metadata,
                };
                component.symbols.push(symbol);
            }
        }
        component.numPins = component.symbols.reduce((p, c) => p + c.pins.length, 0);
        this.project.components[component.name] = component;
    }
    generatePinText(p, pinRef, pointTransform) {
        let nameJustify;
        let pinRefJustify;
        let angle;
        if (p.StartX > p.HotptX) {
            nameJustify = [-1, 0];
            pinRefJustify = [1, 0];
            angle = 0;
        }
        else if (p.StartX < p.HotptX) {
            nameJustify = [1, 0];
            pinRefJustify = [-1, 0];
            angle = 0;
        }
        else {
            if (p.StartY > p.HotptY) {
                nameJustify = [1, 0];
                pinRefJustify = [-1, 0];
                angle = 90;
            }
            else {
                nameJustify = [-1, 0];
                pinRefJustify = [1, 0];
                angle = 90;
            }
        }
        const textSize = 4;
        const estNameTextWidth = p.Name.length * textSize;
        const nameTextOffset = estNameTextWidth / 2;
        const estPinRefTextWidth = pinRef.length * textSize;
        const pinRefTextOffset = estPinRefTextWidth / 2;
        let nameOffset = [0, 0];
        let pinRefOffset = [0, 0];
        switch (angle) {
            case 0:
                nameOffset = [nameTextOffset * -nameJustify[0], 0];
                pinRefOffset = [pinRefTextOffset * -pinRefJustify[0], 0];
                break;
            case 90:
                nameOffset = [0, nameTextOffset * nameJustify[0]];
                pinRefOffset = [0, pinRefTextOffset * pinRefJustify[0]];
                break;
            default:
                break;
        }
        pointTransform = pointTransform ?? ((p) => p);
        return [
            {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinName].id],
                drawing: {
                    type: 'text',
                    value: p.Name,
                    pos: (0, Transform_1.translatePoint)(pointTransform([p.StartX, p.StartY]), nameOffset),
                    angle,
                    justify: [0, 0],
                    size: textSize,
                    thickness: 1,
                },
            },
            {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.PinNumber].id],
                drawing: {
                    type: 'text',
                    value: pinRef,
                    pos: (0, Transform_1.translatePoint)(pointTransform([p.HotptX, p.HotptY]), pinRefOffset),
                    angle,
                    justify: [0, 0],
                    size: textSize,
                    thickness: 1,
                },
            },
        ];
    }
    extractSchematicFromSchematic(design, schematic, sheetIndex) {
        for (const page of schematic.Page ?? []) {
            this.extractSchematicPage(schematic, page, design, sheetIndex);
        }
    }
    extractSchematicPage(schematic, page, design, sheetIndex) {
        const foundPageNumber = page.TitleBlock?.flatMap((titleBlock) => titleBlock.UserProperty ?? []).find((p) => p.Name === 'Page Number')?.Value;
        const sch = {
            modelType: 'schematic',
            designId: `${schematic.Name} - ${page.Name}`,
            sourceFile: design.Name,
            sheetIndex: foundPageNumber ? parseInt(foundPageNumber) - 1 : sheetIndex,
            layerSet: orcadLayers_1.ORCAD_SCH_LAYERSET,
            drawings: {},
            edges_bbox: {
                minx: Number.NEGATIVE_INFINITY,
                miny: Number.NEGATIVE_INFINITY,
                maxx: Number.POSITIVE_INFINITY,
                maxy: Number.POSITIVE_INFINITY,
            },
            initial_zoombox: {
                minx: Number.NEGATIVE_INFINITY,
                miny: Number.NEGATIVE_INFINITY,
                maxx: Number.POSITIVE_INFINITY,
                maxy: Number.POSITIVE_INFINITY,
            },
            font: {
                default: fonts_1.Font.Newstroke,
            },
            ports: {},
            modules: [],
            nets: {},
            warnings: [],
            multiInstanceConfiguration: {
                instances: [],
                multiInstanceModules: [],
            },
        };
        if (!this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET]) {
            this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET] = orcadLayers_1.OrCadSchLayers.map((layer) => {
                layer.defaultVisible = typeof layer.defaultVisible !== 'boolean' || layer.defaultVisible === true;
                return layer;
            });
        }
        this.project.designs[sch.designId] = sch;
        for (const partInst of page.PartInst ?? []) {
            this.extractModuleFromPartInst(sch, partInst);
        }
        for (const globalSymbol of page.Global ?? []) {
            this.extractModuleFromGlobalSymbol(sch, globalSymbol);
        }
        for (const netScalar of page.NetScalar ?? []) {
            this.extractNetFromNetScalar(sch, netScalar);
        }
        for (const netBus of page.NetBus ?? []) {
            this.extractNetFromNetBus(sch, netBus);
        }
        for (const busEntry of page.BusEntry ?? []) {
            this.extractBusEntry(sch, busEntry);
        }
        for (const port of page.Port ?? []) {
            this.extractPort(sch, port);
        }
        for (const offPageConnector of page.OffPageConnector ?? []) {
            this.extractOffPageConnector(sch, offPageConnector);
        }
        for (const commentText of page.GraphicCommentTextInst ?? []) {
            this.extractCommentText(sch, commentText);
        }
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Other].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Other].id].unshift(...[
            ...(page.GraphicLineInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicLineInst()),
            ...(page.GraphicBoxInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicBoxInst()),
            ...(page.GraphicPolygonInst ?? []).flatMap(orcadShapeConverters_1.OrCadShapeConvert.graphicPolygonInst()),
        ]);
        const bbox = new Area_1.default();
        bbox.extendFromSchModel(this.project, sch, this.project.layerSets[orcadLayers_1.ORCAD_SCH_LAYERSET], sch.warnings);
        sch.edges_bbox = {
            minx: Number.isFinite(bbox.xMin) ? bbox.xMin : -100,
            miny: Number.isFinite(bbox.yMin) ? bbox.yMin : -100,
            maxx: Number.isFinite(bbox.xMax) ? bbox.xMax : 100,
            maxy: Number.isFinite(bbox.yMax) ? bbox.yMax : 100,
        };
        sch.initial_zoombox = {
            minx: Number.isFinite(bbox.xMin) ? bbox.xMin : -100,
            miny: Number.isFinite(bbox.yMin) ? bbox.yMin : -100,
            maxx: Number.isFinite(bbox.xMax) ? bbox.xMax : 100,
            maxy: Number.isFinite(bbox.yMax) ? bbox.yMax : 100,
        };
        const moduleIds = sch.modules.map((m) => m.ref);
        if (new Set(moduleIds).size !== moduleIds.length) {
            throw new Error(`Duplicate module ids found in schematic ${sch.designId}`);
        }
    }
    extractModuleFromGlobalSymbol(sch, globalSymbol) {
        const component = this.project.components[globalSymbol.SymbolName];
        if (!component) {
            throw new Error(`Could not find component for global symbol ${globalSymbol.SymbolName}`);
        }
        const symbol = component.symbols.find((s) => s.ref === globalSymbol.SymbolName);
        if (!symbol) {
            throw new Error(`Could not find symbol for global symbol ${globalSymbol.SymbolName}`);
        }
        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
        let center = pointTransform([globalSymbol.LocX, globalSymbol.LocY]);
        if (globalSymbol.Rotation % 2 === 1) {
            center = oneWeirdTrick(center);
        }
        const module = {
            ref: globalSymbol.DbId.toString(),
            footprintRef: symbol.ref,
            componentRef: component.name,
            center,
            layer: 'F',
            transform: {
                pos: [0, 0],
                angle: globalSymbol.Rotation * 90,
                mirrored: globalSymbol.Mirror === 1 ?
                    [
                        globalSymbol.Rotation === 1 || globalSymbol.Rotation === 3,
                        globalSymbol.Rotation === 2 || globalSymbol.Rotation === 0,
                    ]
                    : [false, false],
            },
            name: `#PWR${(this.globalSymbolNum++).toString().padStart(3, '0')}`,
            pinNets: {},
            drawProperties: {},
            metadata: {
                Value: globalSymbol.Name,
                'orcad.globalSymbol.name': globalSymbol.Name,
                'orcad.globalSymbol.symbolName': globalSymbol.SymbolName,
            },
        };
        if (globalSymbol.DisplayProperty) {
            module.drawProperties[globalSymbol.DisplayProperty.Name] = {
                value: globalSymbol.DisplayProperty.Name === 'Name' ? globalSymbol.Name : module.name,
                pos: [
                    globalSymbol.DisplayProperty.LocX + globalSymbol.LocX,
                    globalSymbol.DisplayProperty.LocY + globalSymbol.LocY,
                ],
                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.ValueLabel].id,
                angle: globalSymbol.DisplayProperty.Rotation * 90,
                justify: [-1, -1],
                size: this.scaleFont(globalSymbol.DisplayProperty.Font),
                thickness: 1,
                mirror: [false, false],
                hidden: false,
            };
        }
        this.addSchematicModule(sch, module);
        this.connectGlobalSymbolNets(sch, globalSymbol, symbol, module);
    }
    connectGlobalSymbolNets(sch, globalSymbol, symbol, module) {
        if (symbol.pins.length === 0) {
            this.logProcessResult({
                type: 'global-symbol-no-signals',
                message: `Global symbol ${globalSymbol.Name} symbol ${symbol.ref} has no pins. No signals will be connected.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
            return;
        }
        if (symbol.pins.length > 1) {
            this.logProcessResult({
                type: 'global-symbol-multiple-signals',
                message: `Global symbol ${globalSymbol.Name} symbol ${symbol.ref} has multiple pins. No signals will be connected.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
            return;
        }
        let net = sch.nets[globalSymbol.Name];
        if (!net) {
            net = {
                name: globalSymbol.Name,
                connections: [],
            };
            sch.nets[globalSymbol.Name] = net;
        }
        const pinRef = symbol.pins[0].ref;
        net.connections.push({
            moduleRef: module.ref,
            moduleName: module.name,
            pinRef: pinRef,
        });
        module.pinNets[pinRef] = globalSymbol.Name;
    }
    getInstOccurenceForRefInstId(refInstId) {
        const occurences = [];
        for (const [, schema] of this.logicalSchemas) {
            for (const schemaOccurence of schema.Occurrences.InstOccurrence) {
                for (const occurence of schemaOccurence.InstOccurrence ?? []) {
                    if (occurence.RefInstId === refInstId) {
                        occurences.push([schema, occurence]);
                    }
                }
            }
        }
        if (occurences.length > 1) {
            throw new Error(`${occurences.length} inst occurences found for refInstId ${refInstId}`);
        }
        if (occurences.length === 1) {
            return occurences[0][1];
        }
        return undefined;
    }
    extractModuleFromPartInst(sch, partInst) {
        const refInstId = partInst.DbId.toString();
        const instOccurence = this.getInstOccurenceForRefInstId(refInstId);
        const referenceDesignator = instOccurence?.UserProperty?.find((p) => p.Name === 'Part Reference')?.Value ??
            `${partInst.Reference}${partInst.DeviceDesignator}`;
        const fullModuleName = instOccurence?.UserProperty?.find((p) => p.Name === 'Reference')?.Value ?? partInst.Reference;
        const componentRef = `${partInst.LibName}::${partInst.PkgName}`;
        const component = this.project.components[componentRef];
        if (!component) {
            sch.warnings.push(`Could not find component for ${componentRef} (${referenceDesignator})`);
            throw new Error(`Could not find component for ${componentRef} (${referenceDesignator})`);
        }
        let symbol = component.symbols.find((s) => s.ref === `${partInst.GraphicName}${partInst.DeviceDesignator}`) ??
            component.symbols.find((s) => s.ref === `${partInst.GraphicName}`);
        if (!symbol && (0, alphaDesignatorToConstant_1.isAlphaDesignator)(partInst.DeviceDesignator)) {
            const physicalPartIndex = (0, alphaDesignatorToConstant_1.alphaDesignatorToIndex)(partInst.DeviceDesignator);
            symbol = component.symbols.find((s) => s.name === `${partInst.GraphicName}-${physicalPartIndex}`);
            this.logProcessResult({
                type: 'symbol-lookup-fallback-physical-part-index',
                message: `Found symbol for ${componentRef}::${partInst.GraphicName} (${referenceDesignator}) using backup physical part index ${physicalPartIndex}`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        if (!symbol) {
            sch.warnings.push(`Could not find symbol for ${componentRef}::${partInst.GraphicName} (${referenceDesignator})`);
            return;
        }
        const metadata = {};
        for (const userProp of partInst.UserProperty ?? []) {
            metadata['Value'] = partInst.PartValue;
            metadata[userProp.Name] = userProp.Value;
        }
        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
        let center = pointTransform([partInst.LocX, partInst.LocY]);
        if (partInst.Rotation % 2 === 1) {
            center = oneWeirdTrick(center);
        }
        const partModule = {
            ref: refInstId,
            name: referenceDesignator,
            fullModuleName,
            footprintRef: symbol.ref,
            componentRef: component.name,
            center,
            layer: 'F',
            transform: {
                pos: [0, 0],
                angle: partInst.Rotation * 90,
                mirrored: partInst.Mirror === 1 ?
                    [partInst.Rotation === 1 || partInst.Rotation === 3, partInst.Rotation === 2 || partInst.Rotation === 0]
                    : [false, false],
            },
            pinNets: {},
            drawProperties: {},
            metadata,
        };
        {
            const namedValues = Object.fromEntries([
                ['Part Reference', partInst.Reference],
                ['Value', partInst.PartValue],
                ...Object.entries(partInst.UserProperty ?? []).map(([k, v]) => [k, v.Value]),
            ]);
            for (const prop of partInst.DisplayProperty ?? []) {
                let layerType = sigma_engine_shared_1.SchLayerType.Label;
                switch (prop.Name) {
                    case 'Name':
                        layerType = sigma_engine_shared_1.SchLayerType.NameLabel;
                        break;
                    case 'Value':
                        layerType = sigma_engine_shared_1.SchLayerType.ValueLabel;
                        break;
                }
                const namedValue = namedValues[prop.Name];
                if (namedValue === undefined) {
                    sch.warnings.push(`Could not find value for defined display property ${prop.Name} on ${partInst.Reference}`);
                    continue;
                }
                partModule.drawProperties[prop.Name] = {
                    value: namedValue,
                    pos: [prop.LocX + partInst.LocX, prop.LocY + partInst.LocY],
                    layer: orcadLayers_1.OrCadSchLayerMap[layerType].id,
                    angle: prop.Rotation * 90,
                    justify: [-1, -1],
                    size: this.scaleFont(prop.Font),
                    thickness: 1,
                    hidden: false,
                    ref_designator: prop.Name === 'Part Reference',
                    mirror: [false, false],
                };
            }
        }
        this.addSchematicModule(sch, partModule);
    }
    addSchematicModule(sch, partModule) {
        sch.modules.push(partModule);
        const matchName = partModule.fullModuleName ?? partModule.name;
        let projectModule = this.project.modules.find((m) => m.name === matchName);
        if (!projectModule) {
            projectModule = {
                name: partModule.fullModuleName ?? partModule.name,
                instances: [],
                componentRef: partModule.componentRef,
                value: partModule.metadata?.['Value']?.toString(),
            };
            this.project.modules.push(projectModule);
        }
        if (!projectModule.value && partModule.metadata?.['Value']) {
            projectModule.value = partModule.metadata['Value'].toString();
        }
        if (partModule.metadata) {
            projectModule.metadata = (0, addMetadataWithoutDuplicates_1.addMetadataWithoutDuplicates)(projectModule.metadata, partModule.metadata);
        }
        projectModule.instances.push({
            ref: partModule.ref,
            designId: sch.designId,
        });
    }
    applyNetlist(netlist) {
        if (!netlist.network?.nets?.length) {
            this.logProcessResult({
                type: 'netlist-network-missing',
                message: 'Netlist network section is empty. Module pin nets will not be populated.',
                severity: sigma_engine_shared_1.BoundaryLevel.Error,
            });
            return;
        }
        for (const net of netlist.network?.nets ?? []) {
            this.project.globalNets[net.name] = {
                name: net.name,
            };
            for (const pin of net.pins ?? []) {
                const appliedPins = new Set();
                const module = this.project.modules.find((m) => m.name === pin.referenceDesignator);
                if (!module) {
                    this.logProcessResult({
                        type: 'netlist-module-missing',
                        message: `Netlist references module ${pin.referenceDesignator} that does not exist in project.`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    });
                    continue;
                }
                const component = this.project.components[module.componentRef];
                if (!component) {
                    throw new Error(`Component ${module.componentRef} not found for module ${module.name}`);
                }
                for (const instance of module.instances) {
                    const schematic = this.project.designs[instance.designId];
                    if (!schematic) {
                        throw new Error(`Schematic ${instance.designId} not found for module ${module.name}`);
                    }
                    const schematicModule = schematic.modules.find((m) => m.ref === instance.ref);
                    if (!schematicModule) {
                        throw new Error(`Module ${instance.ref} not found in schematic ${instance.designId} but has an instance pointer`);
                    }
                    const symbol = component.symbols.find((s) => s.ref === schematicModule.footprintRef);
                    if (!symbol) {
                        throw new Error(`Symbol ${schematicModule.footprintRef} not found for component ${component.name} module ${schematicModule.name}`);
                    }
                    const symbolPin = symbol.pins.find((p) => p.number === pin.pin);
                    if (!symbolPin) {
                        continue;
                    }
                    schematicModule.pinNets[symbolPin.ref] = net.name;
                    let schematicNet = schematic.nets[net.name];
                    if (!schematicNet) {
                        schematicNet = {
                            name: net.name,
                            connections: [],
                        };
                        schematic.nets[net.name] = schematicNet;
                    }
                    schematicNet.connections.push({
                        moduleRef: instance.ref,
                        moduleName: module.name,
                        pinRef: symbolPin.ref,
                    });
                    appliedPins.add(pin.pin);
                }
                if (appliedPins.size !== 1) {
                    this.logProcessResult({
                        type: 'netlist-pin-ambiguous',
                        message: `Netlist references pin ${pin.pin} on module ${pin.referenceDesignator} that is ambiguous. Found ${appliedPins.size} matches.`,
                        severity: sigma_engine_shared_1.BoundaryLevel.Error,
                    });
                }
            }
        }
    }
    extractNetFromNetScalar(sch, netScalar) {
        for (const wire of netScalar.WireScalars ?? []) {
            this.extractWireFromWireScalar(sch, wire, false);
        }
    }
    extractNetFromNetBus(sch, netBus) {
        for (const wire of netBus.WireBus ?? []) {
            this.extractWireFromWireScalar(sch, wire, true);
        }
        for (const netScalar of netBus.NetScalar ?? []) {
            this.extractNetFromNetScalar(sch, netScalar);
        }
    }
    extractWireFromWireScalar(sch, wire, bus = false) {
        const layer = bus ? orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus] : orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire];
        const width = bus ? wire.LineWidth : wire.LineWidth * OrCadDSNTransformer.WIRE_WIDTH_MULTIPLIER;
        sch.drawings[layer.id] ??= [];
        sch.drawings[layer.id].push({
            type: 'segment',
            start: [wire.StartX, wire.StartY],
            end: [wire.EndX, wire.EndY],
            width: width,
        });
        const juctionLayer = bus ? orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.BusJunction] : orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Junction];
        wire.Junction?.forEach((j) => {
            sch.drawings[juctionLayer.id] ??= [];
            sch.drawings[juctionLayer.id].push({
                type: 'junction',
                position: [j.LocX, j.LocY],
                radiusOverride: 1.25,
            });
        });
        for (const alias of wire.Alias ?? []) {
            this.extractTextFromAlias(sch, alias);
        }
    }
    extractCommentText(sch, commentText) {
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id].push({
            type: 'text',
            value: commentText.Name,
            pos: [commentText.LocX, commentText.LocY],
            angle: commentText.Rotation ?? 0,
            justify: [-1, 0],
            size: this.scaleFont(commentText.Font),
            thickness: 1,
        });
    }
    extractTextFromAlias(sch, alias) {
        if (!alias.Name || !alias.DbId || !alias.LocX || !alias.LocY || !alias.Font) {
            return;
        }
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id].push({
            type: 'text',
            value: alias.Name,
            pos: [alias.LocX, alias.LocY],
            angle: alias.Rotation ?? 0,
            justify: [-1, 1],
            size: this.scaleFont(alias.Font),
            thickness: 1,
        });
    }
    extractPortSymbol(portSymbol) {
        const symbol = {
            ref: portSymbol.Name,
            name: portSymbol.Name,
            drawings: [],
            pins: [],
            metadata: {
                'orcad.symbol.boundingbox': portSymbol.BoundingBox,
            },
        };
        const bboxCenter = [
            (portSymbol.BoundingBox.X1 + portSymbol.BoundingBox.X2) / 2,
            (portSymbol.BoundingBox.Y1 + portSymbol.BoundingBox.Y2) / 2,
        ];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
        symbol.drawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(portSymbol).map((drawing) => {
            return {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
                drawing,
            };
        });
        this.portSymbols[portSymbol.Name] = symbol;
    }
    extractOffPageSymbol(offPageSymbol) {
        const bboxCenter = [
            (offPageSymbol.BoundingBox.X1 + offPageSymbol.BoundingBox.X2) / 2,
            (offPageSymbol.BoundingBox.Y1 + offPageSymbol.BoundingBox.Y2) / 2,
        ];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
        const symbol = {
            ref: offPageSymbol.Name,
            name: offPageSymbol.Name,
            drawings: [
                ...orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({ pointTransform })(offPageSymbol).map((drawing) => {
                    return {
                        layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
                        drawing,
                    };
                }),
            ],
            pins: [],
            metadata: {
                'orcad.symbol.boundingbox': offPageSymbol.BoundingBox,
            },
        };
        this.offPageSymbols[offPageSymbol.Name] = symbol;
    }
    extractOffPageConnector(sch, connector) {
        const symbol = this.offPageSymbols[String(connector.SymbolName)];
        if (!symbol) {
            sch.warnings.push(`Could not find off page symbol for ${connector.SymbolName}`);
            return;
        }
        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
        let symbolCenterOffset = pointTransform([0, 0]);
        if (connector.Rotation % 2 === 1) {
            symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
        }
        const mirrored = connector.Mirror === 1 ?
            [connector.Rotation === 1 || connector.Rotation === 3, connector.Rotation === 2 || connector.Rotation === 0]
            : [false, false];
        const symbolDrawings = symbol.drawings.map((d) => (0, Transform_1.transformDrawing)((0, Transform_1.transformDrawing)(d.drawing, { mirrored, angle: connector.Rotation * 90, pos: [0, 0] }), {
            mirrored: [false, false],
            angle: 0,
            pos: symbolCenterOffset,
        }));
        const namedValues = Object.fromEntries([
            ['Name', connector.Name],
            ...(connector.OffPageConnectorUserProp ?? []).map((prop) => [prop.Name, prop.Value]),
        ]);
        const textDrawings = (connector.OffPageConnectorDisplayProp ?? []).flatMap((displayProp) => {
            const namedValue = namedValues[displayProp.Name];
            if (namedValue === undefined) {
                sch.warnings.push(`Could not find value for defined display property ${displayProp.Name} on ${connector.SymbolName}`);
                return [];
            }
            const fontSize = this.scaleFont(displayProp.Font);
            const textLoc = [displayProp.LocX, displayProp.LocY];
            return [
                {
                    type: 'text',
                    value: namedValue,
                    pos: (0, Transform_1.translatePoint)(textLoc, [0, fontSize]),
                    angle: displayProp.Rotation * 90,
                    justify: [-1, -1],
                    size: fontSize,
                    mirror: [false, false],
                    thickness: 1,
                },
            ];
        });
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id].push({
            type: 'group',
            pos: [connector.LocX, connector.LocY],
            angle: 0,
            drawings: [...textDrawings, ...symbolDrawings],
            metadata: {},
        });
    }
    extractPort(sch, port) {
        const symbol = this.portSymbols[String(port.SymbolName)];
        if (!symbol) {
            sch.warnings.push(`Could not find port symbol for ${port.SymbolName}`);
            return;
        }
        const symbolbbox = symbol.metadata['orcad.symbol.boundingbox'];
        const bboxCenter = [(symbolbbox.X1 + symbolbbox.X2) / 2, (symbolbbox.Y1 + symbolbbox.Y2) / 2];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [bboxCenter[0], bboxCenter[1]]);
        const oneWeirdTrick = (p) => (0, Transform_1.translatePoint)((0, Transform_1.translatePoint)(p, [bboxCenter[1], bboxCenter[0]]), [-bboxCenter[0], -bboxCenter[1]]);
        let symbolCenterOffset = pointTransform([0, 0]);
        if (port.Rotation && port.Rotation % 2 === 1) {
            symbolCenterOffset = oneWeirdTrick(symbolCenterOffset);
        }
        const mirrored = port.Mirror === 1 ?
            [port.Rotation === 1 || port.Rotation === 3, port.Rotation === 2 || port.Rotation === 0]
            : [false, false];
        const symbolDrawings = symbol.drawings.map((d) => (0, Transform_1.transformDrawing)((0, Transform_1.transformDrawing)(d.drawing, { mirrored, angle: (port.Rotation ?? 0) * 90, pos: [0, 0] }), {
            mirrored: [false, false],
            angle: 0,
            pos: symbolCenterOffset,
        }));
        const namedValues = Object.fromEntries([
            ['Name', port.Name],
            ...(port.PortUserProp ?? []).map((prop) => [prop.Name, prop.Value]),
        ]);
        const textDrawings = (port.PortDisplayProp ?? []).flatMap((displayProp) => {
            const namedValue = namedValues[displayProp.Name];
            if (namedValue === undefined) {
                sch.warnings.push(`Could not find value for defined display property ${displayProp.Name} on ${port.Name}`);
                return [];
            }
            const fontSize = this.scaleFont(displayProp.Font);
            const textLoc = [displayProp.LocX, displayProp.LocY];
            return [
                {
                    type: 'text',
                    value: namedValue,
                    pos: (0, Transform_1.translatePoint)(textLoc, [0, fontSize]),
                    angle: displayProp.Rotation * 90,
                    justify: [-1, -1],
                    size: fontSize,
                    mirror: [false, false],
                    thickness: 1,
                },
            ];
        });
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id].push({
            type: 'group',
            pos: [port.LocX, port.LocY],
            angle: 0,
            drawings: [...textDrawings, ...symbolDrawings],
            metadata: {},
        });
    }
    scaleFont(font) {
        return Math.abs(font.Height / 2);
    }
    extractBusEntry(sch, busEntry) {
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id] ??= [];
        sch.drawings[orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id].push({
            type: 'segment',
            start: [busEntry.BusX, busEntry.BusY],
            end: [busEntry.EndX, busEntry.EndY],
            width: OrCadDSNTransformer.DEFAULT_WIRE_WIDTH,
        });
    }
}
exports.OrCadDSNTransformer = OrCadDSNTransformer;
//# sourceMappingURL=orcadDsnTransformer.js.map